import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Button, Space, Tabs, FormInstance } from 'antd';
import { RadioCardGroup } from '../frontdesk/components/RadioCardGroup';
import { RoomType, Room } from './Rooms';

interface RoomModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  editingRoom: Room | null;
  onSubmit: (values: any) => void;
  loading: boolean;
  roomTypes: RoomType[];
}

export const RoomModal: React.FC<RoomModalProps> = ({
  open,
  onCancel,
  form,
  editingRoom,
  onSubmit,
  loading,
  roomTypes,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const selectedRoomTypeId = Form.useWatch('room_type_id', form);
  const selectedRoomType = roomTypes.find((rt) => rt.id === selectedRoomTypeId);
  const acPreference = selectedRoomType?.ac_status_preference || 'both';

  React.useEffect(() => {
    if (open) {
      const currentAcStatus = form.getFieldValue('ac_status');
      if (acPreference === 'ac' && currentAcStatus !== 'ac') {
        form.setFieldValue('ac_status', 'ac');
      } else if (acPreference === 'non_ac' && currentAcStatus !== 'non_ac') {
        form.setFieldValue('ac_status', 'non_ac');
      } else if (acPreference === 'both' && !currentAcStatus) {
        form.setFieldValue('ac_status', 'both');
      }
    }
  }, [acPreference, form, open]);

  const acOptions = acPreference === 'ac' 
    ? [{ label: 'AC Only', value: 'ac' }] 
    : acPreference === 'non_ac' 
      ? [{ label: 'Non-AC Only', value: 'non_ac' }] 
      : [
          { label: 'AC', value: 'ac' },
          { label: 'Non-AC', value: 'non_ac' },
          { label: 'Both', value: 'both' }
        ];
  const handleNext = async () => {
    try {
      if (activeTab === "1") {
        await form.validateFields([
          "room_number",
          "floor",
          "room_type_id",
          "ac_status"
        ]);
        setActiveTab("2");
      }
    } catch (e) {
      // Validation failed
    }
  };

  const handleModalCancel = () => {
    onCancel();
    setActiveTab("1");
  };

  return (
    <Modal
      title={editingRoom ? 'Edit Room' : 'Add Room'}
      open={open}
      onCancel={handleModalCancel}
      width={760}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button onClick={() => { form.resetFields(); setActiveTab("1"); }}>Reset</Button>
          <Space>
            {activeTab !== "1" && (
              <Button onClick={() => setActiveTab(String(Number(activeTab) - 1))}>Back</Button>
            )}
            {activeTab !== "2" && (
              <Button id="room-next-btn" type="primary" onClick={handleNext}>Next</Button>
            )}
            {activeTab === "2" && (
              <Button id="room-submit-btn" type="primary" loading={loading} onClick={() => form.submit()}>
                {editingRoom ? 'Save Changes' : 'Create Room'}
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{ status: 'available', ac_status: 'both' }}
      >
        <Tabs
          activeKey={activeTab}
          onTabClick={(key) => {
            if (key !== activeTab) {
              const modalBody = document.querySelector('.ant-modal-body');
              if (modalBody) {
                modalBody.scrollTo({ top: modalBody.scrollHeight, behavior: 'smooth' });
              }
              const nextBtn = document.getElementById(activeTab === "2" ? 'room-submit-btn' : 'room-next-btn');
              if (nextBtn) {
                nextBtn.focus();
                nextBtn.style.boxShadow = '0 0 0 4px rgba(24, 144, 255, 0.4)';
                nextBtn.style.transition = 'box-shadow 0.3s ease';
                setTimeout(() => {
                  nextBtn.style.boxShadow = '';
                }, 1000);
              }
            }
            setActiveTab(key);
          }}
          items={[
            {
              key: "1",
              label: "1. Room Details",
              children: (
                <div className="grid-two">
                  <Form.Item
                    label="Room Number"
                    name="room_number"
                    rules={[{ required: true, message: 'Room number is required' }]}
                  >
                    <Input placeholder="e.g., 101, 201A" />
                  </Form.Item>

                  <Form.Item
                    label="Floor"
                    name="floor"
                    rules={[{ required: true, message: 'Floor is required' }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    label="Room Type"
                    name="room_type_id"
                    rules={[{ required: true, message: 'Room type is required' }]}
                    style={{ gridColumn: '1 / -1' }}
                  >
                    <RadioCardGroup
                      options={roomTypes.map((rt) => ({
                        label: rt.name,
                        value: rt.id,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item
                    label="AC Availability"
                    name="ac_status"
                    rules={[{ required: true, message: 'AC Availability is required' }]}
                    style={{ gridColumn: '1 / -1' }}
                  >
                    <RadioCardGroup
                      options={acOptions}
                    />
                  </Form.Item>
                </div>
              )
            },
            {
              key: "2",
              label: "2. Status & Maintenance",
              children: (
                <div className="grid-two">
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: 'Status is required' }]}
                    style={{ gridColumn: '1 / -1' }}
                  >
                    <RadioCardGroup
                      options={[
                        { label: 'Available', value: 'available' },
                        { label: 'Occupied', value: 'occupied' },
                        { label: 'Cleaning', value: 'cleaning' },
                        { label: 'Maintenance', value: 'maintenance' },
                        { label: 'Out of Order', value: 'out_of_order' },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Maintenance Notes"
                    name="maintenance_notes"
                    style={{ gridColumn: '1 / -1' }}
                  >
                    <Input.TextArea rows={4} placeholder="Any maintenance notes or special instructions for this room" />
                  </Form.Item>
                </div>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  );
};
